import {
  ApiError,
  canHoldSession,
  closeBpSession,
  getSession,
  setSession,
  type SignedInSession,
} from '@bp-files/client';
import { env } from '@/env';
import { getMe, postJson, type Me } from '@/lib/api';

/**
 * Who the caller is, at the gate (SYS-8, SYS-10).
 *
 * **`GET /me` is the detector, and MSAL is a ceremony rather than a source of
 * truth.** This module used to be one function that turned a *completed* MSAL
 * sign-in into a Blue Partner session; the gate above it asked MSAL whether
 * anybody was there. That question had the wrong shape from the day the session
 * became a cookie (ADR-0045): MSAL caches in `sessionStorage` on purpose, so it
 * answers per **tab**, while the cookie is per **browser**. A second tab — or
 * one reopened an hour later — held a perfectly good session and was shown
 * "Sign in", and `ssoSilent` existed to paper over exactly that.
 *
 * So the order inverted. `resolveSession` asks the API first: a 200 is a
 * session and a 401 is not, which is the same answer every other request in the
 * app already gets. MSAL is reached only to **finish something this app
 * started** — the passcode round trip — and an anonymous visitor who never
 * clicks the button never downloads it at all.
 *
 * The ordering inside the ceremony branch is unchanged and still matters: the
 * exchange resolves **before** `/me` is fetched, so `/me` is the first request
 * answered by the cookie rather than by the bearer. Under ADR-0049 that is no
 * longer merely tidy — in prod the token is spendable at the exchange and
 * nowhere else, so a `/me` asked in the other order would simply be refused.
 */

/**
 * Who the caller is, or `undefined` when nobody is signed in.
 *
 * **Never navigates**, which is the half of ADR-0034's contract that survives
 * everything else changing: it reports, and the page decides what to render.
 *
 * Throws when it cannot find out — a `/me` that answers 500. That is
 * deliberately distinct from `undefined`: "signed out" offers a sign-in and "we
 * could not tell" offers a retry, and collapsing them would ask somebody to
 * re-authenticate their way out of an outage.
 *
 * **There is nothing else to try, which is what changed in M11.** This function
 * used to have a second half: a ceremony mark in `sessionStorage`, an MSAL
 * chunk download, `handleRedirectPromise`, and a token exchange — five acts for
 * a tab coming back from a Microsoft page, and two ADRs' worth of bugs
 * (ADR-0052) about a mark that had to outlive exactly one page load. The
 * passcode is three POSTs on the landing page now (SYS-7), so a tab never
 * leaves and never comes back, and a 401 means what it says.
 *
 * The `onPhase` parameter went with it. It existed to move the button's label
 * off "Checking your session" for a leg doing far more than checking; there is
 * no such leg.
 */
export async function resolveSession(): Promise<Me | undefined> {
  const held = await probeMe();
  if (held === undefined) return undefined;

  registerSession(held);

  return held;
}

/**
 * `GET /me`, with a 401 as an answer rather than an error.
 *
 * **Only 401 means signed out.** ADR-0004 has the API answer 401 and 403
 * identically-uninformatively on purpose, and a 403 here would mean a validated
 * credential belonging to nobody on this system — which is a state the sign-in
 * button cannot improve. Everything that is not a 401 is therefore "we could not
 * tell", and reaches the caller as a throw.
 */
async function probeMe(): Promise<Me | undefined> {
  try {
    return await getMe();
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 401) return undefined;
    throw cause;
  }
}

/*
  **`settle` used to sit here, and ADR-0083 emptied it.** It did two things
  beyond registering the session: it cleared a `localStorage` hint when `/me`
  reported no credentials, and it set that hint for a **restricted** session —
  the one upward heal ADR-0050 §2 licensed, on the argument that such a person
  demonstrably holds a passkey and demonstrably just took the long way round.

  Both were maintenance on a guess. The landing page offered the passkey only
  when the hint was set, WebAuthn exposes no way to ask whether a credential
  actually exists, and iOS clears the storage the guess lived in often enough
  that the guess was wrong most mornings. The page offers the passkey to every
  visitor now, so there is nothing to teach and nothing to un-teach, and this
  function had one caller and one statement left in it.

  What remains is the rule: **this module writes nothing to storage about the
  browser.** A future flag here would be the same mistake with a new name — the
  question it wanted to answer is one the platform refuses to answer.
*/

/**
 * Make `getSession()` mean *somebody is signed in* — which is what every reader
 * of it already assumed, and what it had quietly stopped meaning.
 *
 * **The bug this exists to not have again.** `msal.ts` is the only writer of the
 * session seam, and since ADR-0050 it is reached **only to finish a passcode
 * ceremony this tab started**. So on the two paths that are now ordinary — a
 * passkey sign-in, and a reload landing on a live cookie — the app came up fully
 * signed in with `getSession()` empty, and everything gated on it silently
 * disappeared: the account menu and the header back button both. It survived
 * review because it is invisible wherever the session cookie cannot exist. Dev
 * was two origins at the time — `VITE_API_BASE_URL` named `localhost:7071`
 * outright — so `canHoldSession` was false, so every load there went through the
 * ceremony and registered a session. Prod is one origin. **The only environment
 * that could show the bug was the only one nobody could test in.**
 *
 * _Amended (ADR-0051)._ Dev points at `/api` through the Vite proxy now, so it
 * takes this branch too and the asymmetry that hid the bug is gone. The guard
 * below is unchanged: it is about whether a cookie *can* exist, not about which
 * environment we happen to be in.
 *
 * Two shapes, one seam:
 *
 * - **An Entra bootstrap** gives a session with a token, and this *wraps* its
 *   sign-out rather than replacing it, because there are two credentials in play
 *   and ending one is a sign-out that does not sign out. The revoke is awaited
 *   first: `signOut()` hands the tab to Entra and never usefully returns, so
 *   anything after it does not run. Get the order backwards and the session row
 *   outlives the browser that owned it.
 *
 * - **A cookie** gives a session with no token at all — the cookie is the
 *   credential and `http.ts` sends no header for it — built from `/me`, because
 *   on this path there is no token to read an address off and `/me` has already
 *   answered. Signing out revokes the row and returns to the origin; there is no
 *   Entra session in this tab to end after it.
 *
 * **Only where a cookie can exist**, which is `canHoldSession`'s question and
 * not a new one. Cross-origin there is no cookie to revoke, and a session
 * registered on this branch would be a claim with nothing behind it — including
 * under mocks, where nothing registers one on purpose (Article 5, ADR-0027).
 *
 * Idempotent: registering twice would revoke twice, which is harmless, but the
 * guard keeps the registered session's identity stable for anything comparing
 * it.
 */
let registered = false;

function registerSession(me: Me): void {
  if (registered) return;

  const bootstrap = getSession();

  if (bootstrap !== undefined) {
    setSession({
      ...bootstrap,
      signOut: () => {
        void closeBpSession(postJson, env.VITE_API_BASE_URL, env.VITE_SIMULATED_AUTH).finally(
          () => {
            bootstrap.signOut();
          },
        );
      },
    });
    registered = true;
    return;
  }

  // Not `registered = true` on the way out: nothing was registered, so a later
  // call is free to try again.
  if (!canHoldSession(env.VITE_API_BASE_URL, env.VITE_SIMULATED_AUTH)) return;

  /*
    **And never under mocks**, which used to be free and stopped being free when
    dev moved to one origin (ADR-0051). `canHoldSession('/api')` is true in a
    mocked build now, so without this line the branch below would run there — and
    its `signOut` revokes a row MSW invents and then navigates, while
    `bp.mock.signed-in` sits in `sessionStorage` untouched. The tab would come
    back signed in from a sign-out that reported success.

    Mocked builds register no session on purpose (`lib/mockAuth.ts`), the header
    is absent because of it, and `MockPersonaSwitch` in the environment banner is
    the identity affordance instead. That bargain is ADR-0027's Article 5 and is
    unrelated to what this change is about.
  */
  if (env.VITE_USE_MOCKS) return;

  const cookieSession: SignedInSession = {
    email: me.user.email,
    displayName: me.user.name,
    signOut: () => {
      void closeBpSession(postJson, env.VITE_API_BASE_URL).finally(() => {
        // A real document navigation, not a router push: the point is to come
        // back with no session, and `main.tsx`'s boot is what discovers that.
        window.location.assign('/');
      });
    },
  };

  setSession(cookieSession);
  registered = true;
}

/** Drop the registration. Tests only — `test/setup.ts` already resets the session itself. */
export function resetBpSessionForTests(): void {
  registered = false;
}
